import type { ReactNode } from "react";

interface PageHeaderProps {
  title: string;
  /** Plain string or rich inline content (e.g. a line with bold/colored spans). */
  subtitle?: ReactNode;
  /** Optional eyebrow line above the title (e.g. a date or section label). */
  eyebrow?: string;
  /** Right-aligned actions (e.g. primary/secondary buttons). */
  actions?: ReactNode;
}

/**
 * Standard page-level header: an optional eyebrow + h1 title + optional
 * subtitle, with optional right-aligned actions. Shared by top-level pages and
 * settings tab pages so every screen reads the same (see Clients/Invoices/
 * Practitioners). The title uses the display font (Bricolage Grotesque).
 */
export function PageHeader({ title, subtitle, eyebrow, actions }: PageHeaderProps) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && (
          <span className="text-muted-foreground text-sm font-medium tracking-[0.02em]">
            {eyebrow}
          </span>
        )}
        <h1 className="text-foreground font-heading text-3xl font-semibold tracking-[-0.02em]">
          {title}
        </h1>
        {subtitle && <p className="text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
